import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/utils/AuthContext';
import { pb } from '@/utils/pocketbase';
import { API_URL } from '@/utils/config';
import { useLanguage } from '@/i18n/LanguageContext';
import type { Persona } from '@/utils/pocketbase';

export interface PersonaFormState {
  selectedProjectId: string;
  targetAudience: string;
  selectedAnalysisId: string;
  gender: string;
  age: string;
}

export interface UsePersonaActionsReturn {
  isLoading: boolean;
  isFree: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCreateNew: boolean;
  handleCreatePersona: (
    formState: PersonaFormState,
    callbacks: {
      setError: (error: string) => void;
      onSuccess: (persona: Persona) => void;
      onLoadData: () => Promise<unknown>;
      resetForm: () => void;
    }
  ) => Promise<void>;
  handleDeletePersona: (
    personaId: string,
    callbacks: {
      setError: (error: string) => void;
      isCurrentPersona: boolean;
      onClearCurrent: () => void;
      onLoadData: () => Promise<unknown>;
    }
  ) => Promise<void>;
  handleEditPersona: (
    persona: Persona,
    callbacks: {
      setError: (error: string) => void;
      onEdit: (persona: Persona) => void;
    }
  ) => void;
}

export function usePersonaActions(personasCount: number): UsePersonaActionsReturn {
  const navigate = useNavigate();
  const { t } = useTranslation('personas');
  const { lang } = useLanguage();
  const { tier: subscriptionTier, isPremium } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);

  const isFree = subscriptionTier === 'free';
  const canEdit = !isFree;
  const canDelete = !isFree;
  const canCreateNew = !isFree || personasCount < 1;

  const handleCreatePersona = async (
    formState: PersonaFormState,
    callbacks: {
      setError: (error: string) => void;
      onSuccess: (persona: Persona) => void;
      onLoadData: () => Promise<unknown>;
      resetForm: () => void;
    }
  ) => {
    if (!formState.selectedProjectId) {
      callbacks.setError(t('actions.error.selectProduct'));
      return;
    }

    const isFreeCheck = subscriptionTier === 'free';
    if (isFreeCheck && personasCount >= 1) {
      callbacks.setError(t('actions.error.freeLimitReached'));
      navigate('/subscription');
      return;
    }

    setIsLoading(true);
    callbacks.setError('');

    try {
      const response = await fetch(`${API_URL}/api/ai/personas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({
          projectId: formState.selectedProjectId,
          target_audience: formState.targetAudience || undefined,
          market_analysis_id: formState.selectedAnalysisId || undefined,
          gender: formState.gender || undefined,
          age: formState.age || undefined,
          lang,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          callbacks.setError(data.message || t('actions.error.limitReached'));
          navigate('/subscription');
        } else {
          callbacks.setError(data.error || t('actions.error.createFailed'));
        }
        return;
      }

      callbacks.onSuccess(data.persona);
      await callbacks.onLoadData();
      callbacks.resetForm();
    } catch (err) {
      console.error('Error creating persona:', err);
      callbacks.setError(t('actions.error.createFailedRetry'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePersona = async (
    personaId: string,
    callbacks: {
      setError: (error: string) => void;
      isCurrentPersona: boolean;
      onClearCurrent: () => void;
      onLoadData: () => Promise<unknown>;
    }
  ) => {
    if (!isPremium) {
      callbacks.setError(t('actions.error.cannotDeleteFree'));
      return;
    }

    if (!confirm(t('actions.confirm.delete'))) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/ai/personas/${personaId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${pb.authStore.token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        callbacks.setError(data.error || t('actions.error.deleteFailed'));
        return;
      }

      if (callbacks.isCurrentPersona) {
        callbacks.onClearCurrent();
      }

      await callbacks.onLoadData();
    } catch (err) {
      console.error('Error deleting persona:', err);
      callbacks.setError(t('actions.error.deleteFailed'));
    }
  };

  const handleEditPersona = (
    persona: Persona,
    callbacks: {
      setError: (error: string) => void;
      onEdit: (persona: Persona) => void;
    }
  ) => {
    if (!isPremium) {
      callbacks.setError(t('actions.error.cannotEditFree'));
      return;
    }

    callbacks.onEdit(persona);
  };

  return {
    isLoading,
    isFree,
    canEdit,
    canDelete,
    canCreateNew,
    handleCreatePersona,
    handleDeletePersona,
    handleEditPersona,
  };
}
